<template>
  <div
    v-if="isMobile ? !sectionSettings.hideMobile : !sectionSettings.hideDesktop"
    ref="container"
    class="media-carousel critical-component-clear"
    :class="[
      {
        'media-carousel--half': contentPlacement === 'half',
      },
    ]"
    :style="`${sectionSettingsBackgroundStyles};
    --padding-bottom-mobile: ${sectionSettings.paddingBottomMobile}px;
    --padding-top-mobile: ${sectionSettings.paddingTopMobile}px;
    --padding-bottom-desktop: ${sectionSettings.paddingBottomDesktop}px;
    --padding-top-desktop: ${sectionSettings.paddingTopDesktop}px;
    `"
  >
    <div
      ref="carouselContainer"
      class="media-carousel__slide-container media-carousel__carousel splide"
      :js-carousel="`media-carousel-${sectionSettings.id}`"
      :js-sticky-child="!isMobile && contentPlacement === 'half'"
      js-sticky-position="top"
    >
      <h2
        v-if="sectionSettings.titleText !== '' && sectionSettings.titlePosition === 'top'"
        :class="[
          {
            [$class('hidden')]: isMobile ?
              sectionSettings.hideTitleMobile : sectionSettings.hideTitleDesktop
          },
        ]"
        class="
            media-carousel__title
            media-carousel__title--top
            text-desktop-medium
            text-mobile-medium
          "
        v-text="sectionSettings.titleText"
      />

      <div class="media-carousel__content">
        <div class="media-carousel__track splide__track">
          <div class="splide__list">
            <carousel-slide
              v-for="(slide, index) of slides"
              :key="`carousel-slide-${sectionSettings.id}-${index}`"
              class="splide__slide"
              :class="[`sectionSettings-content--${slide.contentColor}`]"
              :slide="slide"
              :section-settings-settings="slide"
              :carousel-index="carouselIndex"
              :element-index="index"
              :content-placement="contentPlacement"
              :media-link="slide.mediaLink"
              :preload-image="sectionSettings.preloadImage"
              :lazy-media="sectionSettings.lazyMedia"
              :high-priority-media="sectionSettings.highPriorityMedia"
              @carousel-image-loaded="
                (loadedIndex) => imagesLoaded.push(`image-${loadedIndex}`)
              "
            />
          </div>
        </div>
      </div>

      <div
        class="splide__arrows media-carousel__controls"
      >
        <button
          v-show="slides?.length > 1"
          class="
                splide__arrow
                splide__arrow--prev
                media-carousel__prev
              "
          type="button"
          :aria-label="$string('accessibility.carousel.previous')"
        >
          <icon-arrow-left-thin />
        </button>

        <button
          v-show="slides?.length > 1"
          class="
                splide__arrow
                splide__arrow--next
                media-carousel__next
              "
          type="button"
          :aria-label="$string('accessibility.carousel.next')"
        >
          <icon-arrow-right-thin />
        </button>
      </div>

      <div
        v-if="sectionSettings.titleText !== '' || sectionSettings.bodyText !== ''"
        class="media-carousel__footer"
      >
        <h2
          v-if="sectionSettings.titleText !== '' && sectionSettings.titlePosition === 'bottom'"
          :class="[
            {
              [$class('hidden')]: isMobile ?
                sectionSettings.hideTitleMobile : sectionSettings.hideTitleDesktop
            },
          ]"
          class="
            media-carousel__title
            text-desktop-regular
            text-mobile-regular
          "
        >
          {{ sectionSettings.titleText }}
        </h2>

        <div
          v-if="sectionSettings.bodyText !== ''"
          class="media-carousel__body text-desktop-regular text-mobile-regular"
          v-html="sectionSettings.bodyText"
        />

        <a
          v-if="sectionSettings.button?.text !== '' && sectionSettings.button?.url !== ''"
          class="media-carousel__button text-desktop-small text-mobile-small text-regular"
          :href="sectionSettings.button.url"
          v-text="sectionSettings.button?.text"
        />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Media carousel (media-carousel)
 * -----------------------------------------------------------------------------
 * Banner component containing slides in a carousel set to full-height.
 *
 * @param {String} contentPlacement - String indicating if the component is
 * occupying half or full width.
 * @param {Object} sectionSettings - Object containing sectionSettings settings
 * @param {Array} slides - Array containing blocks settings
 */

import { mapState } from 'vuex'

import Carousel from '~/api/carousel'

import CarouselSlide from '~async/media-carousel/carousel-slide/carousel-slide'
import IconArrowRightThin from '~icons/direction/arrow/right-thin.svg'
import IconArrowLeftThin from '~icons/direction/arrow/left-thin.svg'

export default {
  name: 'MediaCarousel',

  components: {
    CarouselSlide,
    IconArrowLeftThin,
    IconArrowRightThin,
  },

  props: {
    contentPlacement: {
      type: String,
      default: 'full',
    },

    sectionSettings: {
      type: Object,
      default: () => ({
        id: '',
        backgroundColor: '',
        hideDesktop: '',
        hideMobile: '',
        hideTitleDesktop: '',
        hideTitleMobile: '',
        carousel: {
          autoplay: false,
          autoplaySpeed: null,
        },
        titleText: '',
        titlePosition: 'bottom',
        bodyText: '',
        buttonText: '',
        buttonURL: '',
        paddingTopDesktop: '',
        paddingTopMobile: '',
        paddingBottomDesktop: '',
        paddingBottomMobile: '',
        preloadImage: false,
        lazyMedia: true,
        highPriorityMedia: false,
      }),
    },

    slides: {
      type: Array,
      default: () => [
        {
          mobileMediaSettings: {
            mobileDefaultHeight: '',
            mobileVideoFile: '',
            mobileVimeoId: '',
            mobileImage: '',
          },
          desktopMediaSettings: {
            desktopDefaultHeight: '',
            desktopVideoFile: '',
            desktopVimeoId: '',
            desktopImage: '',
          },
          contentColor: '',
          mediaLink: '',
        },
      ],
    },
  },

  data() {
    return {
      carousel: null,
      carouselIndex: 0,
      carouselMounted: false,
      findParent: false,
      imagesLoaded: [],
      splideOptions: {
        arrows: true,
        gap: 'var(--spacing-6)',
        perMove: 1,
        perPage: 1,
        padding: this.contentPlacement === 'half' ? '0px' : '192px',
        type: 'fade',
        rewind: true,
        speed: 1000,
      },
      splideOptionsMobile: {
        fixedWidth: '100%',
        type: 'loop',
        rewind: true,
        drag: 'free',
        gap: 'var(--spacing-3)',
        padding: { left: 'var(--spacing-3)', right: 'var(--spacing-3)' },
        perMove: 1,
        perPage: 1,
        speed: 1000,
        snap: true,
        pagination: true,
        classes: {
          pagination: 'splide__pagination media-carousel__pagination',
          page: 'splide__pagination__page media-carousel__page',
        },
      },
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      breakpoint: (state) => state.index.screen.breakpoint,
    }),

    /**
     * Returns formatted carousel index number
     * @returns {String|Number}
     */
    formattedCarouselIndex() {
      const displayPage = this.carouselIndex + 1
      return displayPage < 10 ? `0${displayPage}` : displayPage
    },

    /**
     * Returns formatted carousel total number
     * @returns {String|Number}
     */
    formattedTotal() {
      return this.slides.length < 10
        ? `0${this.slides.length}`
        : this.slides.length
    },

    /**
     * Returns the background style info based on where the component is in
     * its process of fully loading.
     * @returns {String}
     */
    sectionSettingsBackgroundStyles() {
      return this.carouselMounted
        ? `background-color: ${this.sectionSettings.backgroundColor}`
        : 'background-color: transparent'
    },

    /**
     * Returns slide content colour
     * @returns {String}
     */
    slideContentColour() {
      return this.slides[this.carouselIndex].contentColor
    },
  },

  watch: {

    /**
     * When the slide changes we need to reset the cta position, as some slides
     * may have a sticky CTA and some do not.
     */
    carouselIndex() {
      const event = new CustomEvent('carousel-updated', {
        detail: {},
        bubbles: true,
        cancelable: true,
        composed: false,
      })

      this.$nextTick().then(() => {
        document.dispatchEvent(event)
      })
    },

    /**
     * Hacky but we only want to create the carousel when the first image is
     * loaded and available to render. Prevents a flash when the carousel loads
     * over the top of the liquid layer.
     */
    imagesLoaded: {
      deep: true,
      handler() {
        if (!this.imagesLoaded.includes('image-0')) {
          return
        }

        if (this.carousel) {
          return
        }
        this.createCarousel()
      },
    },
  },

  mounted() {
    this.$nextTick().then(() => {
      this.findParent = true
    })

    setTimeout(() => {
      this.setCarouselHeight()

      if (!this.carousel) {
        this.createCarousel()
      }
    }, this.$timing('slow'))
  },

  methods: {

    /**
     * Removes the liquid placeholders AFTER the vue media has loaded
     * and fully rendered.
     */
    removePlaceholders() {
      // Find placeholder within this component's section only
      const section = this.$el?.closest('section')
      const placeholder = section?.querySelector('.liquid-removed')

      if (placeholder) {
        placeholder.remove()
      }
    },

    /**
     * Sets carousel height when sticky to ensure carousel is visible.
     */
    setCarouselHeight() {
      if (this.contentPlacement !== 'half') {
        return
      }

      const carousel = this.$refs.carouselContainer
      const container = this.$refs.container

      container.style.minHeight = `${carousel.offsetHeight}px`
    },

    /**
     * Main carousel contructor function
     */
    createCarousel() {
      const container = this.$refs.carouselContainer
      const carouselNamespace = `[js-carousel="media-carousel-${this.sectionSettings.id}"]`

      if (!container || !carouselNamespace) {
        return
      }

      const reducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)',
      ).matches

      const autoplay = Boolean(
        !reducedMotion && this.sectionSettings.carousel.autoplay,
      )
      const interval = autoplay
        ? this.sectionSettings.carousel.autoplaySpeed
        : null
      const options = this.isMobile
        ? this.splideOptionsMobile
        : this.splideOptions

      this.carousel = Carousel(carouselNamespace, {
        ...options,
        autoplay,
        interval,
        classes: {
          pagination: 'splide__pagination media-carousel__pagination',
          page: 'splide__pagination__page media-carousel__page',
        },
      })

      /**
       * When a slide updates, save the index to data.
       */
      this.carousel.on('moved', (slide) => {
        this.carouselIndex = parseInt(slide)
      })

      /**
       * When the active slide is changed, save the index to data.
       */
      this.carousel.on('active', (slide) => {
        if (!slide.isClone) {
          this.carouselIndex = parseInt(slide.index)
        }
      })

      /**
       * When the carousel mounts update its status.
       */
      this.carousel.on('mounted', () => {
        setTimeout(() => {
          this.carouselMounted = true
          this.removePlaceholders()
          this.setCarouselHeight()
        }, 1)
      })

      this.carousel.mount()
    },
  },
}
</script>

<style lang="scss">
@import './media-carousel';
</style>
